import React, { useState, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  TextField,
  Grid,
  Chip
} from '@mui/material';
import ErrorDisplay from './utils/ErrorDisplay';
import { companiesApi, executionAnalyticsApi } from '@/services/api';
import createPlotlyComponent from 'react-plotly.js/factory';
import Plotly from 'plotly.js-dist-min';
const Plot = createPlotlyComponent(Plotly);
import { ExecutionAnalyticsResponse, SelectOption } from '@/types/client';
import { PageFrame, PageHeader, Surface, Button, Placeholder, FilterAsyncSelect } from '@/components/ui';
import { chartInk, chartSeries, chartSemantic } from '@/theme/chartColors';

interface ChartVisibility {
  [key: string]: boolean;
}

// Memoized Custom Legend Component
const CustomLegend = React.memo(({
  payload,
  visibility,
  onToggle
}: {
  payload?: any[],
  visibility: ChartVisibility,
  onToggle: (dataKey: string) => void
}) => {
  if (!payload) return null;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
      {payload.map((entry: any) => (
        <Chip
          key={entry.dataKey}
          label={entry.value}
          size="small"
          onClick={() => onToggle(entry.dataKey)}
          sx={{
            backgroundColor: visibility[entry.dataKey] ? entry.color : 'var(--surface-3)',
            color: visibility[entry.dataKey] ? 'var(--on-fill)' : 'var(--text-dim)',
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
            },
            transition: 'all 0.2s ease-in-out',
          }}
        />
      ))}
    </Box>
  );
});

CustomLegend.displayName = 'CustomLegend';

// Removed custom tooltip (Plotly provides built-in hover tooltips)

// Removed Recharts custom dot as Plotly handles markers

// Memoized Chart Components
const POVChart = React.memo(({
  data,
  visibility,
  onToggleVisibility
}: {
  data: any[],
  visibility: ChartVisibility,
  onToggleVisibility: (dataKey: string) => void
}) => {
  const legendPayload = useMemo(() => [
    { dataKey: 'buy_pov', value: 'Buy POV', color: chartSemantic.positive },
    { dataKey: 'sell_pov', value: 'Sell POV', color: chartSemantic.negative },
    { dataKey: 'total_pov', value: 'Total POV', color: chartSeries[0] },
  ], []);

  return (
    <Surface
      title="Daily Percentage of Volume (POV)"
      meta="Daily trading volume as percentage of total market volume"
      actions={
        <CustomLegend
          payload={legendPayload}
          visibility={visibility}
          onToggle={onToggleVisibility}
        />
      }
      padded
    >
        <Box sx={{ width: '100%', height: 450 }}>
          <Plot
            data={[
              ...(visibility.buy_pov ? [{
                x: data.map((d) => d.date),
                y: data.map((d) => d.buy_pov),
                type: 'scatter',
                mode: 'lines+markers',
                name: 'Buy POV',
                line: { color: chartSemantic.positive, width: 2 },
                marker: { color: chartSemantic.positive, size: 6 },
                hovertemplate: '%{y:.2f}%<extra>Buy POV</extra>'
              }] : []),
              ...(visibility.sell_pov ? [{
                x: data.map((d) => d.date),
                y: data.map((d) => d.sell_pov),
                type: 'scatter',
                mode: 'lines+markers',
                name: 'Sell POV',
                line: { color: chartSemantic.negative, width: 2 },
                marker: { color: chartSemantic.negative, size: 6 },
                hovertemplate: '%{y:.2f}%<extra>Sell POV</extra>'
              }] : []),
              ...(visibility.total_pov ? [{
                x: data.map((d) => d.date),
                y: data.map((d) => d.total_pov),
                type: 'scatter',
                mode: 'lines+markers',
                name: 'Total POV',
                line: { color: chartSeries[0], width: 2 },
                marker: { color: chartSeries[0], size: 6 },
                hovertemplate: '%{y:.2f}%<extra>Total POV</extra>'
              }] : []),
            ] as any}
            layout={{
              autosize: true,
              height: 450,
              margin: { l: 80, r: 30, t: 10, b: 60 },
              dragmode: 'zoom',
              
              xaxis: {
                type: 'date',
                tickfont: { size: 12, color: chartInk.axis },
                gridcolor: chartInk.grid,
              },
              yaxis: {
                autorange: true,
                automargin: true,
                tickformat: ',.0f',
                ticksuffix: '%',
                tickfont: { size: 12, color: chartInk.axis },
                gridcolor: chartInk.grid,
              },
              hovermode: 'x unified',
              hoverlabel: { bgcolor: chartInk.surface, bordercolor: chartInk.grid, font: { color: chartInk.strong } },
              showlegend: false,
            }}
            config={{ responsive: true, displaylogo: false, scrollZoom: true, modeBarButtonsToRemove: ['select2d', 'lasso2d'] }}
            style={{ width: '100%', height: '100%' }}
          />
        </Box>
    </Surface>
  );
});

POVChart.displayName = 'POVChart';

const VWAPChart = React.memo(({
  data,
  visibility,
  onToggleVisibility
}: {
  data: any[],
  visibility: ChartVisibility,
  onToggleVisibility: (dataKey: string) => void
}) => {
  const legendPayload = useMemo(() => [
    { dataKey: 'vwap', value: 'VWAP', color: chartSeries[0] },
    { dataKey: 'buy_avg_price', value: 'Buy Avg Price', color: chartSemantic.positive },
    { dataKey: 'sell_avg_price', value: 'Sell Avg Price', color: chartSemantic.negative },
  ], []);

  // Removed manual domain; let Plotly autorange

  return (
    <Surface
      title="Volume Weighted Average Price (VWAP)"
      meta="Daily VWAP trend over the selected period"
      actions={
        <CustomLegend
          payload={legendPayload}
          visibility={visibility}
          onToggle={onToggleVisibility}
        />
      }
      padded
    >
        <Box sx={{ width: '100%', height: 450 }}>
          <Plot
            data={[
              ...(visibility.vwap ? [{
                x: data.map((d) => d.date),
                y: data.map((d) => d.vwap),
                type: 'scatter',
                mode: 'lines',
                name: 'VWAP',
                line: { color: chartSeries[0], width: 3 },
                hovertemplate: '₹%{y:.2f}<extra>VWAP</extra>'
              }] : []),
              ...(visibility.buy_avg_price ? [{
                x: data.map((d) => d.date),
                y: data.map((d) => d.buy_avg_price),
                type: 'scatter',
                mode: 'markers',
                name: 'Buy Avg Price',
                marker: { color: chartSemantic.positive, size: 6 },
                hovertemplate: '₹%{y:.2f}<extra>Buy Avg Price</extra>'
              }] : []),
              ...(visibility.sell_avg_price ? [{
                x: data.map((d) => d.date),
                y: data.map((d) => d.sell_avg_price),
                type: 'scatter',
                mode: 'markers',
                name: 'Sell Avg Price',
                marker: { color: chartSemantic.negative, size: 6 },
                hovertemplate: '₹%{y:.2f}<extra>Sell Avg Price</extra>'
              }] : []),
            ] as any}
            layout={{
              autosize: true,
              height: 450,
              margin: { l: 90, r: 30, t: 10, b: 60 },
              dragmode: 'zoom',
              
              xaxis: {
                type: 'date',
                tickfont: { size: 12, color: chartInk.axis },
                gridcolor: chartInk.grid,
              },
              yaxis: {
                autorange: true,
                tickprefix: '₹',
                tickformat: ',.0f',
                automargin: true,
                tickfont: { size: 12, color: chartInk.axis },
                gridcolor: chartInk.grid,
              },
              hovermode: 'x unified',
              hoverlabel: { bgcolor: chartInk.surface, bordercolor: chartInk.grid, font: { color: chartInk.strong } },
              showlegend: false,
            }}
            config={{ responsive: true, displaylogo: false, scrollZoom: true, modeBarButtonsToRemove: ['select2d', 'lasso2d'] }}
            style={{ width: '100%', height: '100%' }}
          />
        </Box>
    </Surface>
  );
});

VWAPChart.displayName = 'VWAPChart';

const BuyVsVWAPChart = React.memo(({
  data,
  visibility,
  onToggleVisibility
}: {
  data: any[],
  visibility: ChartVisibility,
  onToggleVisibility: (dataKey: string) => void
}) => {
  const legendPayload = useMemo(() => [
    { dataKey: 'buy_avg_price_vs_vwap', value: 'Buy Avg Price vs VWAP', color: chartSemantic.positive },
  ], []);

  return (
    <Surface
      title="Buy Orders: Average Price vs VWAP"
      meta="Comparison of buy order average prices against VWAP"
      actions={
        <CustomLegend
          payload={legendPayload}
          visibility={visibility}
          onToggle={onToggleVisibility}
        />
      }
      padded
    >
        <Box sx={{ width: '100%', height: 450 }}>
          <Plot
            data={[
              ...(visibility.buy_avg_price_vs_vwap ? [{
                x: data.map((d) => d.date),
                y: data.map((d) => d.buy_avg_price_vs_vwap),
                type: 'scatter',
                mode: 'lines+markers',
                name: 'Buy Avg Price vs VWAP',
                line: { color: chartSemantic.positive, width: 2 },
                marker: { color: chartSemantic.positive, size: 6 },
                hovertemplate: '%{y:.2f} bps<extra>Buy Avg Price vs VWAP</extra>'
              }] : []),
            ] as any}
            layout={{
              autosize: true,
              height: 450,
              margin: { l: 90, r: 30, t: 10, b: 60 },
              dragmode: 'zoom',
              
              xaxis: {
                type: 'date',
                tickfont: { size: 12, color: chartInk.axis },
                gridcolor: chartInk.grid,
              },
              yaxis: {
                zeroline: true,
                zerolinecolor: chartInk.axis,
                zerolinewidth: 1,
                tickformat: ',.2~f',
                ticksuffix: ' bps',
                automargin: true,
                tickfont: { size: 12, color: chartInk.axis },
                gridcolor: chartInk.grid,
              },
              hovermode: 'x unified',
              hoverlabel: { bgcolor: chartInk.surface, bordercolor: chartInk.grid, font: { color: chartInk.strong } },
              showlegend: false,
            }}
            config={{ responsive: true, displaylogo: false, scrollZoom: true, modeBarButtonsToRemove: ['select2d', 'lasso2d'] }}
            style={{ width: '100%', height: '100%' }}
          />
        </Box>
    </Surface>
  );
});

BuyVsVWAPChart.displayName = 'BuyVsVWAPChart';

const SellVsVWAPChart = React.memo(({
  data,
  visibility,
  onToggleVisibility
}: {
  data: any[],
  visibility: ChartVisibility,
  onToggleVisibility: (dataKey: string) => void
}) => {
  const legendPayload = useMemo(() => [
    { dataKey: 'sell_avg_price_vs_vwap', value: 'Sell Avg Price vs VWAP', color: chartSemantic.negative },
  ], []);

  return (
    <Surface
      title="Sell Orders: Average Price vs VWAP"
      meta="Comparison of sell order average prices against VWAP"
      actions={
        <CustomLegend
          payload={legendPayload}
          visibility={visibility}
          onToggle={onToggleVisibility}
        />
      }
      padded
    >
        <Box sx={{ width: '100%', height: 450 }}>
          <Plot
            data={[
              ...(visibility.sell_avg_price_vs_vwap ? [{
                x: data.map((d) => d.date),
                y: data.map((d) => d.sell_avg_price_vs_vwap),
                type: 'scatter',
                mode: 'lines+markers',
                name: 'Sell Avg Price vs VWAP',
                line: { color: chartSemantic.negative, width: 2 },
                marker: { color: chartSemantic.negative, size: 6 },
                hovertemplate: '%{y:.2f} bps<extra>Sell Avg Price vs VWAP</extra>'
              }] : []),
            ] as any}
            layout={{
              autosize: true,
              height: 450,
              margin: { l: 90, r: 30, t: 10, b: 60 },
              dragmode: 'zoom',
              
              xaxis: {
                type: 'date',
                tickfont: { size: 12, color: chartInk.axis },
                gridcolor: chartInk.grid,
              },
              yaxis: {
                zeroline: true,
                zerolinecolor: chartInk.axis,
                zerolinewidth: 1,
                tickformat: ',.2~f',
                ticksuffix: ' bps',
                automargin: true,
                tickfont: { size: 12, color: chartInk.axis },
                gridcolor: chartInk.grid,
              },
              hovermode: 'x unified',
              hoverlabel: { bgcolor: chartInk.surface, bordercolor: chartInk.grid, font: { color: chartInk.strong } },
              showlegend: false,
            }}
            config={{ responsive: true, displaylogo: false, scrollZoom: true, modeBarButtonsToRemove: ['select2d', 'lasso2d'] }}
            style={{ width: '100%', height: '100%' }}
          />
        </Box>
    </Surface>
  );
});

SellVsVWAPChart.displayName = 'SellVsVWAPChart';

const ExecutionAnalyticsDashboard: React.FC = () => {
  const [selectedCompany, setSelectedCompany] = useState<SelectOption | null>(null);
  // this calendar year
  const [fromDate, setFromDate] = useState<string>(
    new Date(new Date().getFullYear(), 0, 2).toISOString().split('T')[0] // Jan 1st of current year
  );
  const [toDate, setToDate] = useState<string>(
    new Date().toISOString().split('T')[0] // today's date
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analyticsData, setAnalyticsData] = useState<ExecutionAnalyticsResponse | null>(null);

  // Chart visibility states
  const [povVisibility, setPovVisibility] = useState<ChartVisibility>({
    buy_pov: true,
    sell_pov: true,
    total_pov: false,
  });

  const [vwapVisibility, setVwapVisibility] = useState<ChartVisibility>({
    vwap: true,
    buy_avg_price: true,
    sell_avg_price: true,
  });

  const [buyVsVwapVisibility, setBuyVsVwapVisibility] = useState<ChartVisibility>({
    buy_avg_price_vs_vwap: true,
  });

  const [sellVsVwapVisibility, setSellVsVwapVisibility] = useState<ChartVisibility>({
    sell_avg_price_vs_vwap: true,
  });

  // Server-searched company options for the picker below — replaces an eager
  // page_size: 10000 fetch of every company on mount.
  const loadCompanyOptions = useCallback(async (search: string): Promise<SelectOption[]> => {
    try {
      const companiesData = await companiesApi.getCompanies({ search, page_size: 100 });
      const uniqueCompanies = companiesData.results.filter((company, index, self) =>
        index === self.findIndex(c => `${c.symbol} - ${c.name}` === `${company.symbol} - ${company.name}`)
      );
      return uniqueCompanies.map(company => ({
        value: String(company.id),
        label: `${company.symbol} - ${company.name}`
      }));
    } catch (err: any) {
      console.error('Error loading companies:', err);
      if (err.code === 'ERR_NETWORK') {
        setError('Connection with Server Failed!');
      }
      return [];
    }
  }, []);

  // Memoized toggle handlers
  const handlePovToggle = useCallback((dataKey: string) => {
    setPovVisibility(prev => ({ ...prev, [dataKey]: !prev[dataKey] }));
  }, []);

  const handleVwapToggle = useCallback((dataKey: string) => {
    setVwapVisibility(prev => ({ ...prev, [dataKey]: !prev[dataKey] }));
  }, []);

  const handleBuyVsVwapToggle = useCallback((dataKey: string) => {
    setBuyVsVwapVisibility(prev => ({ ...prev, [dataKey]: !prev[dataKey] }));
  }, []);

  const handleSellVsVwapToggle = useCallback((dataKey: string) => {
    setSellVsVwapVisibility(prev => ({ ...prev, [dataKey]: !prev[dataKey] }));
  }, []);

  // Load analytics data
  const loadAnalytics = useCallback(async () => {
    if (!selectedCompany || !fromDate || !toDate) {
      toast.warning('Please select a company and date range');
      return;
    }

    if (new Date(fromDate) > new Date(toDate)) {
      toast.error('From date cannot be greater than To date');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await executionAnalyticsApi.getAnalytics({
        from_date: fromDate,
        to_date: toDate,
        company_id: parseInt(selectedCompany.value)
      });
      if (response.status >= 400) {
        throw new Error(response.statusText);
      }
      const companyAnalytics = response.data?.data?.company_analytics || [];
      if (companyAnalytics.length === 0) {
        toast.info('No analytics data found for the selected filters');
        setAnalyticsData(null);
        return;
      }

      setAnalyticsData(companyAnalytics[0]);
    } catch (err: any) {
      const errorMessage = JSON.stringify(err.response?.data) || String(err) || 'Failed to load analytics data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, fromDate, toDate]);

  const clearFilters = useCallback(() => {
    setSelectedCompany(null);
    setFromDate('');
    setToDate('');
    toast.info('Filters cleared');
  }, []);

  return (
    <PageFrame
      header={
        <PageHeader
          title="Execution Analytics"
          subtitle="How our fills compared with the market: participation, VWAP, and price against VWAP."
        />
      }
    >
        <Surface padded>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
              <FilterAsyncSelect
                label="Companies"
                value={selectedCompany}
                onChange={setSelectedCompany}
                loadOptions={loadCompanyOptions}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
              <TextField
                type="date"
                label="From Date"
                variant="outlined"
                size="small"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                sx={{ width: '100%' }}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 2 }}>
              <TextField
                type="date"
                label="To Date"
                variant="outlined"
                size="small"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                slotProps={{
                  inputLabel: {
                    shrink: true,
                  },
                }}
                sx={{ width: '100%' }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6, lg: 6 }}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Button variant="primary" onClick={loadAnalytics} disabled={loading}>
                  {loading ? 'Loading…' : 'Apply Filters'}
                </Button>
                <Button onClick={clearFilters}>Clear Filters</Button>
              </Box>
            </Grid>
          </Grid>
        </Surface>

        {loading ? (
            <Surface>
              <Placeholder kind="loading" message="Loading execution analytics…" />
            </Surface>
        ) : error ? (
            <ErrorDisplay message={error} type='error' onRetry={loadAnalytics} />
        ) : analyticsData ? (
            <Grid container spacing={3}>
              {/* Daily POV Chart */}
              {analyticsData.daily_pov && analyticsData.daily_pov.length > 0 && <Grid size={{ xs: 12, md: 6 }}>
                <POVChart
                  data={analyticsData.daily_pov}
                  visibility={povVisibility}
                  onToggleVisibility={handlePovToggle}
                />
              </Grid>}

              {/* VWAP Chart */}
              {analyticsData.vwap_data && analyticsData.vwap_data.length > 0 && <Grid size={{ xs: 12, md: 6 }}>
                <VWAPChart
                  data={analyticsData.vwap_data}
                  visibility={vwapVisibility}
                  onToggleVisibility={handleVwapToggle}
                />
              </Grid>}

              {/* Buy Price vs VWAP Chart */}
              {analyticsData.buy_price_vs_vwap && analyticsData.buy_price_vs_vwap.length > 0 && <Grid size={{ xs: 12, md: 6 }}>
                <BuyVsVWAPChart
                  data={analyticsData.buy_price_vs_vwap}
                  visibility={buyVsVwapVisibility}
                  onToggleVisibility={handleBuyVsVwapToggle}
                />
              </Grid>}

              {/* Sell Price vs VWAP Chart */}
              {analyticsData.sell_price_vs_vwap && analyticsData.sell_price_vs_vwap.length > 0 && <Grid size={{ xs: 12, md: 6 }}>
                <SellVsVWAPChart
                  data={analyticsData.sell_price_vs_vwap}
                  visibility={sellVsVwapVisibility}
                  onToggleVisibility={handleSellVsVwapToggle}
                />
              </Grid>}
            </Grid>
        ) : (
            <Surface>
              <Placeholder
                message="No execution analytics yet"
                hint="Pick a company and date range above, then click Apply Filters."
              />
            </Surface>
        )}
    </PageFrame>
  );
};

export default ExecutionAnalyticsDashboard;